import { EAnalysisStages, EAnalysisStatus } from "@/constants/enums";
import {
  AIAnalysisResponse,
  AIHiringTeamResponse,
  IAnalysisProgress,
  IJobAnalysis
} from "@/lib/database/schema/jobanalysis";
import { AIRecruiterLensResponseType } from "@/lib/integrations/ai/prompts";

export class JobAnalysis implements IJobAnalysis {
  id?: string;
  user_id: string | null;
  job_id: string | null;
  linkedin_job_id: string;
  linkedin_job_url?: string | null;
  ai_analysis?: AIAnalysisResponse & AIHiringTeamResponse;
  ai_analyzed_at?: string;
  ai_analyzed: boolean;
  applied?: boolean;
  applied_at?: string;
  analysis_progress?: IAnalysisProgress;
  ai_recruiter_lens_analysis?: AIRecruiterLensResponseType;
  custom_cv_id?: string | null; // Optional field for custom CV ID
  created_at?: string;
  updated_at?: string;

  constructor(analysisData: IJobAnalysis) {
    this.id = analysisData.id;
    this.user_id = analysisData.user_id;
    this.job_id = analysisData.job_id;
    this.linkedin_job_id = analysisData.linkedin_job_id;
    this.linkedin_job_url = analysisData.linkedin_job_url;
    this.ai_analysis = analysisData.ai_analysis;
    this.ai_analyzed_at = analysisData.ai_analyzed_at;
    this.ai_analyzed = analysisData.ai_analyzed;
    this.ai_recruiter_lens_analysis = analysisData.ai_recruiter_lens_analysis;
    this.applied = analysisData.applied;
    this.applied_at = analysisData.applied_at;
    this.analysis_progress = analysisData.analysis_progress;
    this.custom_cv_id = analysisData.custom_cv_id || null; // Ensure custom_cv_id is initialized
    this.created_at = analysisData.created_at;
    this.updated_at = analysisData.updated_at;
  }

  /**
   * Check if analysis is completed
   */
  isAnalysisComplete(): boolean {
    return this.ai_analyzed && !!this.ai_analysis;
  }

  /**
   * Check if analysis is in progress
   */
  isAnalysisInProgress(): boolean {
    return this.analysis_progress?.status === EAnalysisStatus.IN_PROGRESS;
  }

  /**
   * Check if analysis has failed
   */
  isAnalysisFailed(): boolean {
    return this.analysis_progress?.status === EAnalysisStatus.FAILED;
  }

  /**
   * Check if analysis is pending
   */
  isAnalysisPending(): boolean {
    return this.analysis_progress?.status === EAnalysisStatus.PENDING;
  }

  isAnalysisDone(): boolean {
    return this.analysis_progress?.status === EAnalysisStatus.DONE;
  }

  /**
   * Check if user has applied to this job
   */
  hasApplied(): boolean {
    return !!this.applied;
  }

  /**
   * Get match score from AI analysis
   */
  getMatchScore(): number {
    return this.ai_analysis?.match_score_number || 0;
  }

  /**
   * Get match score category
   */
  getMatchScoreCategory(): string {
    const score = this.getMatchScore();
    if (score >= 80) return 'Excellent Match';
    if (score >= 60) return 'Good Match';
    if (score >= 40) return 'Fair Match';
    if (score >= 20) return 'Poor Match';
    return 'Not a Match';
  }

  /**
   * Check if analysis has salary information
   */
  hasSalaryInfo(): boolean {
    return !!(this.ai_analysis?.salary?.min_base_salary && this.ai_analysis?.salary?.max_base_salary);
  }

  /**
   * Get formatted salary range
   */
  getSalaryRange(): string {
    if (!this.hasSalaryInfo()) return 'Not Available';
    
    const salary = this.ai_analysis!.salary;
    const currency = salary.currency || 'USD';
    const period = salary.period || 'year';
    
    return `${currency} ${salary.min_base_salary?.toLocaleString()} - ${salary.max_base_salary?.toLocaleString()} per ${period}`;
  }

  /**
   * Check if analysis has hiring team data
   */
  hasHiringTeamData(): boolean {
    return !!(this.ai_analysis?.outreach_list && this.ai_analysis.outreach_list.length > 0);
  }

  /**
   * Get number of hiring team contacts
   */
  getHiringTeamContactCount(): number {
    return this.ai_analysis?.outreach_list?.length || 0;
  }

  /**
   * Get analysis progress percentage
   */
  getProgressPercentage(): number {
    return this.analysis_progress?.progress_percentage || 0;
  }

  /**
   * Get current analysis stage
   */
  getCurrentStage(): EAnalysisStages | undefined {
    return this.analysis_progress?.stage;
  }

  /**
   * Get analysis status
   */
  getStatus(): EAnalysisStatus | undefined {
    return this.analysis_progress?.status;
  }

  /**
   * Get error message if analysis failed
   */
  getErrorMessage(): string | undefined {
    return this.analysis_progress?.error;
  }

  /**
   * Get progress message
   */
  getProgressMessage(): string | undefined {
    return this.analysis_progress?.message;
  }

  /**
   * Check if analysis has overlapping skills
   */
  hasOverlappingSkills(): boolean {
    return !!(this.ai_analysis?.overlapping_skills_list && this.ai_analysis.overlapping_skills_list.length > 0);
  }

  /**
   * Check if analysis has new skills to learn
   */
  hasNewSkills(): boolean {
    return !!(this.ai_analysis?.new_skills_list && this.ai_analysis.new_skills_list.length > 0);
  }

  /**
   * Get number of overlapping skills
   */
  getOverlappingSkillsCount(): number {
    return this.ai_analysis?.overlapping_skills_list?.length || 0;
  }

  /**
   * Get number of new skills
   */
  getNewSkillsCount(): number {
    return this.ai_analysis?.new_skills_list?.length || 0;
  }

  /**
   * Check if analysis has actionable next steps
   */
  hasNextSteps(): boolean {
    return !!(this.ai_analysis?.next_steps_list && this.ai_analysis.next_steps_list.length > 0);
  }

  /**
   * Check if analysis has helpful resources
   */
  hasHelpfulResources(): boolean {
    return !!(this.ai_analysis?.helpful_resources_list && this.ai_analysis.helpful_resources_list.length > 0);
  }

  /**
   * Get days since analysis was completed
   */
  getDaysSinceAnalysis(): number | null {
    if (!this.ai_analyzed_at) return null;
    
    const analysisDate = new Date(this.ai_analyzed_at);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - analysisDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Check if analysis is recent (within 30 days)
   */
  isAnalysisRecent(): boolean {
    const days = this.getDaysSinceAnalysis();
    return days !== null && days <= 30;
  }

  hasCustomCV(): boolean {
    return !!this.custom_cv_id; 
  }

  /**
   * Check if the analysis is stale (older than 10 minutes)
   */
  isStale(): boolean {
    if (!this.created_at) {
      // No date? Consider stale to be safe
      return true;
    }
    const createdAt = new Date(this.created_at).getTime();
    const now = Date.now();
    const diffMs = now - createdAt;
    const diffMinutes = diffMs / (1000 * 60);

    // Stale if older than 10 minutes and either failed or in progress without AI analysis
    return diffMinutes > 10 && (!this.isAnalysisDone()) && (!this.isAnalysisComplete());
  }
  
  /**
   * Convert back to IJobAnalysis interface
   */
  toIJobAnalysis(): IJobAnalysis {
    return {
      id: this.id,
      user_id: this.user_id,
      job_id: this.job_id,
      linkedin_job_id: this.linkedin_job_id,
      linkedin_job_url: this.linkedin_job_url,
      ai_analysis: this.ai_analysis,
      ai_analyzed_at: this.ai_analyzed_at,
      ai_analyzed: this.ai_analyzed,
      applied: this.applied,
      applied_at: this.applied_at,
      analysis_progress: this.analysis_progress,
      created_at: this.created_at,
      updated_at: this.updated_at,
      ai_recruiter_lens_analysis: this.ai_recruiter_lens_analysis,
      custom_cv_id: this.custom_cv_id || null, // Ensure custom_cv_id is always defined
    };
  }
}
